import { create } from 'zustand';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: string;
  action: string;
  userId: string;
  userDisplayName: string | null;
  organizationId: string;
  workspaceId: string | null;
  entityId: string | null;
  details: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AuditLogResponse {
  items: AuditLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogFilters {
  workspaceId?: string;
  action?: string;
  userId?: string;
  from?: string;
  to?: string;
}

interface AuditLogState {
  entries: AuditLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filters: AuditLogFilters;
  loading: boolean;
  error: string | null;
  setFilters: (filters: AuditLogFilters) => void;
  setPage: (page: number) => void;
  fetch: (getAccessToken: () => Promise<string | null>) => Promise<void>;
}

// ─── Store ──────────────────────────────────────────────────────────────────────

export const useAuditLogStore = create<AuditLogState>((set, get) => ({
  entries: [],
  totalCount: 0,
  page: 1,
  pageSize: 25,
  totalPages: 0,
  filters: {},
  loading: false,
  error: null,

  setFilters: (filters) => set({ filters, page: 1 }),

  setPage: (page) => set({ page }),

  fetch: async (getAccessToken) => {
    set({ loading: true, error: null });

    try {
      const { page, pageSize, filters } = get();
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (filters.workspaceId) params.set('workspaceId', filters.workspaceId);
      if (filters.action) params.set('action', filters.action);
      if (filters.userId) params.set('userId', filters.userId);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      const token = await getAccessToken();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`/api/dotnet/audit-logs?${params.toString()}`, { headers });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = (await response.json()) as AuditLogResponse;
      set({
        entries: data.items,
        totalCount: data.totalCount,
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
        loading: false,
      });
    } catch {
      set({ error: 'Audit-Logs konnten nicht geladen werden.', loading: false });
    }
  },
}));
